/* eslint-disable no-nested-ternary */
import { AnimatePresence, motion } from 'framer-motion'
import LoadingContainer from 'components/LoadingContainer'
import useMediaQuery from 'hooks/useMediaQuery'
import { usePrepareCart } from 'hooks/usePrepareCart'
import useSignedUser from 'hooks/useSignedUser'
import { ArrowLeft } from 'lucide-react'
import Button from 'new-components/Button'
import Heading from 'new-components/Heading'
import Modal from 'new-components/Modal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FcApproval } from 'react-icons/fc'
import { useHistory } from 'react-router'
import { BRANDS_WITH_REQUIRED_FREIGHT, CLASS_WITHOUT_SHIPPING, MOBILE_WIDTH } from 'utils/constants'
import { ProgressiveGroupSection } from '@cannect/new-components/ProgressiveGroupCard/ProgressiveGroupSection'
import useQueryString from '../../../hooks/useQueryString'
import { ContentModal, WrapperModal } from '../styles'
import {
  AddressStep,
  CartSection,
  CartSteps,
  OrderSummary,
  OrderSummaryStep3,
  PrescriptionRequiredModal,
  RelatedProducts,
  WarningBanners
} from './components'
import { PaymentStep } from './components/PaymentStep'
import { PaymentFinish } from './components/paymentFinish'

function PageCartProductsV2() {
  useMediaQuery(MOBILE_WIDTH)
  const {
    isUpdate,
    // updateCart,
    lastOrderCreated,
    setOrderId,
    clearOrderData,
    clearCart,
    removeCart,
    incrementOrDecrementCart,
    // addToCart,
    updateCartCoupon,
    getLastOrder,
    coupon,
    removeCoupon,
    showCartModifiedModal,
    setShowCartModifiedModal,
    cartChanges,
    loading
  } = usePrepareCart()

  const { filters } = useQueryString()
  const withFilter = filters?.cart_id
  const history = useHistory()

  const isModalPayment = lastOrderCreated?.payments?.some((payment: any) => payment.status === 'paid')
  const [isModal, setIsModal] = useState(isModalPayment)
  const [isModalRecipeWarning, setIsModalRecipeWarning] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const [addressData, setAddressData] = useState<any>(null)
  const modalRecipeWarningKey = '@CANNECT:RECIPE_WARNING'
  const cartItems = lastOrderCreated?.items || []

  const previewItems = useMemo(
    () =>
      cartItems
        .map((item: any) => ({
          product_id: Number(item?.product_id || item?.product?.id || item?.id),
          quantity: Number(item?.quantity || 1)
        }))
        .filter((i: { product_id: number; quantity: number }) => i.product_id > 0),
    [cartItems]
  )
  const [groupedProductIds, setGroupedProductIds] = useState<Set<number>>(new Set())
  const handleGroupedIdsChange = useCallback((ids: Set<number>) => setGroupedProductIds(ids), [])
  const lastPersonIdRef = useRef<number | null>(null)
  const lastUserIdRef = useRef<number | null>(null)
  const { signedUser } = useSignedUser()

  // Limpa dados do localStorage quando o usuário muda
  useEffect(() => {
    const currentUserId = signedUser?.id
    const LAST_USER_ID_KEY = '@CANNECT:LAST_USER_ID'
    const storedLastUserId = localStorage.getItem(LAST_USER_ID_KEY)
      ? Number(localStorage.getItem(LAST_USER_ID_KEY))
      : null

    if (currentUserId && storedLastUserId !== null && currentUserId !== storedLastUserId) {
      localStorage.removeItem(modalRecipeWarningKey)
      setAddressData(null)
      setCurrentStep(1)
      setSelectedPaymentMethod('')
    }

    if (currentUserId) {
      lastUserIdRef.current = currentUserId
    } else if (!currentUserId && lastUserIdRef.current !== null) {
      localStorage.removeItem(modalRecipeWarningKey)
      lastUserIdRef.current = null
    }
  }, [signedUser?.id, modalRecipeWarningKey])

  useEffect(() => {
    const currentPersonId = lastOrderCreated?.person?.id
    if (currentPersonId !== lastPersonIdRef.current) {
      setAddressData(null)
      lastPersonIdRef.current = currentPersonId || null
    }
  }, [lastOrderCreated?.person?.id])

  const isAllSupplements = cartItems.every(
    (item: any) =>
      CLASS_WITHOUT_SHIPPING.includes(item?.product?.class_id || item?.class_id) &&
      !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const isAllTestesGeneticos = cartItems.every((item: any) => item?.product?.type === 'TESTE_GENETICO')

  const hasControlledItems = cartItems.some((item: any) => item?.requires_controlled_prescription)
  const allControlledItems =
    cartItems.length > 0 && cartItems.every((item: any) => item?.requires_controlled_prescription)

  const isPillOnly = lastOrderCreated?.is_pill_only ?? false

  const pageContainerRef = useRef<HTMLDivElement | null>(null)
  const topAnchorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (withFilter) {
      getLastOrder()
    }
  }, [withFilter])

  useEffect(() => {
    if (withFilter) return
    if (!isUpdate && (!lastOrderCreated?.items?.length || !cartChanges)) {
      getLastOrder()
    }
  }, [withFilter, lastOrderCreated?.items?.length, isUpdate, cartChanges])

  useEffect(() => {
    if (isModalPayment) {
      if (selectedPaymentMethod === 'card_pix') return
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
      setOrderId(null)
      clearOrderData()

      if (withFilter) {
        history.push(`/analise-cannect-wa?cart_id=${withFilter}`)
      } else if (lastOrderCreated?.uuid) {
        history.push(`/analise-cannect-wa?cart_id=${lastOrderCreated.uuid}`)
      } else {
        history.push('/analise-cannect')
      }
    }
  }, [isModalPayment, withFilter, history, lastOrderCreated, selectedPaymentMethod, clearOrderData, setOrderId])

  // modal de prescrição — aguarda o pedido carregar antes de avaliar para não
  // abrir o modal antes de isPillOnly estar disponível (causaria flash e fechamento abrupto)
  useEffect(() => {
    if (!lastOrderCreated?.id) return
    if (isAllSupplements || isAllTestesGeneticos) return
    if (!hasControlledItems) return

    const oneHourInMilli = 60 * 60 * 1000
    const actualDateInMilli = Date.now()
    const modalRecipeWarning = localStorage.getItem(modalRecipeWarningKey)

    if (!modalRecipeWarning) {
      setIsModalRecipeWarning(true)
    } else if (actualDateInMilli >= parseInt(modalRecipeWarning || '0', 10) + oneHourInMilli) {
      localStorage.removeItem(modalRecipeWarningKey)
      setIsModalRecipeWarning(true)
    }
  }, [lastOrderCreated?.id])

  useEffect(() => {
    requestAnimationFrame(() => {
      if (topAnchorRef.current) {
        try {
          topAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
          return
        } catch {
          // noop
        }
      }

      const scroller =
        pageContainerRef.current || document.scrollingElement || document.documentElement || document.body

      try {
        scroller.scrollTo({ top: 0, behavior: 'smooth' })
      } catch {
        scroller.scrollTop = 0
      }
    })
  }, [currentStep])

  const closeModalRecipeWarning = () => {
    const actualDateInMilli = Date.now()
    setIsModalRecipeWarning(false)
    localStorage.setItem(modalRecipeWarningKey, JSON.stringify(actualDateInMilli))
  }

  const handleNoPrescription = () => {
    closeModalRecipeWarning()
    const cartId = withFilter || lastOrderCreated?.uuid || ''
    history.push(`/agendamento${cartId ? `/${cartId}` : ''}`)
  }

  const closeModal = () => {
    setIsModal(false)
    if (withFilter) {
      history.push(`/analise-cannect-wa?cart_id=${withFilter}`)
    } else {
      history.push(`/analise-cannect`)
    }
  }

  const goToProducts = () => history.push(`/produtos/1`)

  const handleProceedToPayment = () => setCurrentStep(2)

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber <= currentStep) setCurrentStep(stepNumber)
  }

  // Handlers: remover item, remover tudo, atualizar quantidade
  const handleRemoveItem = (index: number) => {
    const item = cartItems[index]
    if (!item) return
    const id = item?.product?.id ?? item?.product_id ?? item?.id
    if (id) removeCart(id)
  }

  const handleRemoveAll = () => {
    clearCart()
  }

  const handleUpdateQuantity = (index: number, quantity: number) => {
    const item = cartItems[index]
    if (!item || quantity <= 0) return
    const id = item?.product?.id ?? item?.product_id ?? item?.id
    if (id) incrementOrDecrementCart(id, quantity)
  }

  const formMethods = useForm<{ cvv1: string; cvv2: string }>({ defaultValues: { cvv1: '', cvv2: '' } })

  const panelMotion = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { type: 'spring', stiffness: 260, damping: 22 }
  }

  const DISTRIBUTORS_HUB = [1, 7]

  const containsProductWithLongerDeliveryTime = cartItems.some(
    (item: any) => item?.product?.stock === 0 && DISTRIBUTORS_HUB.includes(item?.product?.distributor_id)
  )
  const containsProductWithHubDistributor = cartItems.some((item: any) =>
    DISTRIBUTORS_HUB.includes(item?.product?.distributor_id)
  )
  const containProductsMatirz = cartItems.some((item: any) => !DISTRIBUTORS_HUB.includes(item?.product?.distributor_id))

  if (loading && !cartItems.length) return <LoadingContainer loading />

  return (
    <FormProvider {...formMethods}>
      <div ref={pageContainerRef} className="min-h-screen w-full bg-gray-50">
        <div ref={topAnchorRef} aria-hidden className="h-0 w-0" />

        <div className="mx-auto w-full max-w-7xl px-2 py-4 md:px-6 lg:px-8">
          <div className="flex w-full flex-col items-start justify-between gap-4 border-0 border-gray-200 pb-6 lg:flex-row lg:items-center">
            <CartSteps currentStep={currentStep} onStepClick={handleStepClick} />
          </div>

          {currentStep > 1 && (
            <div className="mb-4 flex justify-between px-2 md:px-0">
              <button
                type="button"
                onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
                className="inline-flex items-center gap-2 rounded-md border-0 bg-transparent py-2 text-sm font-medium text-primary-800 hover:underline">
                <ArrowLeft className="h-5 w-5" />
                Voltar
              </button>
            </div>
          )}

          <div className="w-full overflow-hidden">
            <div className="flex flex-col gap-4 md:gap-6 lg:grid lg:grid-cols-12 lg:gap-8">
              {/* COLUNA ESQUERDA */}
              <div className="w-full min-w-0 lg:col-span-7">
                <motion.div layout>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`step-left-${currentStep}`}
                      initial={panelMotion.initial}
                      animate={panelMotion.animate}
                      exit={panelMotion.exit}
                      transition={panelMotion.transition}
                      className="space-y-6">
                      {currentStep === 1 ? (
                        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                          <CartSection
                            itens={cartItems}
                            onRemoveItem={handleRemoveItem}
                            onRemoveAll={handleRemoveAll}
                            onUpdateQuantity={handleUpdateQuantity}
                            isUpdate={isUpdate}
                            coupon={coupon}
                            excludeProductIds={groupedProductIds}
                          />
                          <ProgressiveGroupSection
                            cartItems={previewItems}
                            onGroupedIdsChange={handleGroupedIdsChange}
                            mode="full-sheetrow"
                          />
                          {!cartItems.every(
                            (i: any) =>
                              CLASS_WITHOUT_SHIPPING.includes(i?.product?.class_id || i?.class_id) &&
                              !BRANDS_WITH_REQUIRED_FREIGHT.includes(i?.product?.brand_id || i?.brand_id)
                          ) && (
                            <WarningBanners
                              containsProductWithLongerDeliveryTime={containsProductWithLongerDeliveryTime}
                              containProductsMatirz={containProductsMatirz}
                              containsProductWithHubDistributor={containsProductWithHubDistributor}
                              isPillOnly={isPillOnly}
                            />
                          )}
                        </div>
                      ) : currentStep === 2 ? (
                        <AddressStep
                          onNextStep={() => setCurrentStep(3)}
                          onPreviousStep={() => setCurrentStep(1)}
                          onAddressDataChange={setAddressData}
                          initialAddressData={addressData}
                        />
                      ) : currentStep === 3 ? (
                        <PaymentStep
                          onNextStep={() => setCurrentStep(4)}
                          onPreviousStep={() => setCurrentStep(2)}
                          onPaymentMethodSelect={setSelectedPaymentMethod}
                        />
                      ) : (
                        <PaymentFinish
                          selectedMethod={selectedPaymentMethod}
                          cartItems={cartItems}
                          shippingAddress={addressData}
                        />
                      )}

                      {currentStep === 1 && <RelatedProducts onAddToCart={() => {}} />}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* COLUNA DIREITA */}
              <div className="w-full min-w-0 lg:col-span-5">
                <div className="sticky top-4">
                  <motion.div layout>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`step-right-${currentStep}`}
                        initial={panelMotion.initial}
                        animate={panelMotion.animate}
                        exit={panelMotion.exit}
                        transition={panelMotion.transition}>
                        {currentStep === 1 ? (
                          <OrderSummary
                            itens={cartItems}
                            onApplyCoupon={updateCartCoupon}
                            onProceedToPayment={handleProceedToPayment}
                            isUpdate={isUpdate}
                            coupon={coupon}
                            onRemoveCoupon={removeCoupon}
                            lastOrderCreated={lastOrderCreated}
                          />
                        ) : (
                          <OrderSummaryStep3
                            itens={cartItems}
                            onApplyCoupon={updateCartCoupon}
                            isUpdate={isUpdate}
                            currentStep={currentStep}
                            onNextStep={() => setCurrentStep(currentStep === 2 ? 3 : 4)}
                            onPreviousStep={() => setCurrentStep(1)}
                            addressData={addressData}
                            onRemoveCoupon={removeCoupon}
                            coupon={coupon}
                            selectedPaymentMethod={selectedPaymentMethod}
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de prescrição obrigatória — só renderiza quando há itens com requires_controlled_prescription */}
      {!isAllSupplements && !isAllTestesGeneticos && hasControlledItems && (
        <PrescriptionRequiredModal
          isOpen={isModalRecipeWarning && !isModal}
          onClose={closeModalRecipeWarning}
          onNoPrescription={handleNoPrescription}
          hasControlledItems={hasControlledItems}
          allControlledItems={allControlledItems}
          isPillOnly={isPillOnly}
        />
      )}

      <Modal isOpen={isModal} setOpenModal={closeModal}>
        <WrapperModal>
          <FcApproval size={40} />
          <Heading elementType="h2" defaultColor="darkGrey" fontSize="20px">
            Obrigado!
          </Heading>
          <ContentModal>
            <p>O seu pedido já foi processado e pago com sucesso.</p>
            <Button onClick={() => goToProducts()}>Buscar novos produtos</Button>
            <Button onClick={() => closeModal()}>Ver detalhes do pedido</Button>
          </ContentModal>
        </WrapperModal>
      </Modal>

      {/* <CartModifiedModal
        isOpen={showCartModifiedModal}
        setOpenModal={setShowCartModifiedModal}
        cartChanges={cartChanges}
      /> */}
    </FormProvider>
  )
}

export default PageCartProductsV2
